import "../styles/groups.scss";
import { useMemo, useState } from "react";
import PageLayout from "../components/page-layout";
import Card from "../components/card";
import { GuiButton } from "../components/global-ui/gui-button";
import actions from "../actions/actions";
import { SearchBox } from "../components/search-box";
import Popover from "../components/popover";
import {
  CheckBoxHeaderButton,
  GuiCheckbox,
} from "../components/global-ui/gui-checkbox";
import Grid, { ColumnHeader, GridCell } from "../components/grid";
import { FilterDropdown } from "../components/filter-dropdown";
import { useOnFirstLoad } from "../utilities/hooks";
import { useSelector } from "react-redux";
import {
  downloadFile,
  pluralize,
  useUpdateState,
} from "../utilities/utilities";
import { Modal } from "../components/modal";
import { EditMultiple } from "../components/edit-multiple";
import { ColumnPicker } from "../components/column-picker";
import { Icon, SeparatorDot } from "../components/icon";
import { FilterTagBar } from "../components/filter-tag-bar";
import { MergeGroupsModal } from "../components/merge-groups-modal";
import { dispatch } from "../redux/redux-config";
import { appState } from "../redux/redux-config";
import { storage } from "../utilities/storage";
import { GroupPropertiesModal } from "../components/group-properties";
import { useIsDesktop, UserRole } from "../utilities/constants";
import BottomNavBar from "./BottomNavBar";
import { FilterPopup } from "../components/filter-popup";
import { showToast } from "../components/toast";
import { DISCARD_ROUTES } from "../utilities/constants";
import navigate from "../utilities/navigation";
import { Path } from "../utilities/constants";

let noResultsToastShown = false;

export default function GroupsPage({ history, match }) {
  const userId = match && match.params && match.params.id;
  const personName = JSON.parse(localStorage.getItem("selectedPerson"));
  useOnFirstLoad(actions.groups.initPage);
  let loginUser = useSelector((store) => store.user);
  let { locationOptions, gridColumns, groupSettingsForUser } = useSelector(
    (store) => store.groups,
  );
  let groupTypeOptions =
    useSelector((store) => actions.groups.loadGroupTypes()) || [];
  const currentUser = useSelector((store) => store.user);
  const userRole = currentUser?.userType;
  const isAdmin = userRole === UserRole.Admin;
  const isStaff = userRole === UserRole.Staff;
  const canManageGroups = isAdmin;
  const showSelection = isAdmin;
  let isMobile = useSelector((store) => store.page.isMobile);
  let [checkboxState, updateCheckboxState, setCheckboxState] = useUpdateState(
    {},
  );
  let locationName = groupSettingsForUser
    ? groupSettingsForUser.locationName || "Location"
    : "";
  let [flyoverGroup, setFlyoverGroup] = useState(null);

  let [state, updateState] = useUpdateState(() => {
    const saved = appState().groups.savedFilter;
    return {
      search: "",
      filters: saved?.filters || [],
      sort: saved?.sort || {},
      gridReset: 0,
      editMultipleItems: [{ name: "Merge Groups" }, { name: "Delete Groups" }],
    };
  });

  let filterOptions = [
    { name: "Location", options: locationOptions || [] },
    { name: "Group Type", options: groupTypeOptions || [] },
  ];
  let selectedRows = Object.keys(checkboxState).filter((k) => checkboxState[k]);
  let { sort } = state;
  let disabled = selectedRows.length === 0;
  gridColumns = gridColumns || [];
  let visibility = useMemo(() => {
    let vis = {};
    gridColumns.forEach((c) => (vis[c.column] = c.show));
    return vis;
  }, [gridColumns]);

  async function loadRows(page, pageSize, skipTotal) {
    let sort = Object.keys(state.sort)
      .map((col) => ({ column: col, asc: state.sort[col] }))
      .filter((s) => s.asc !== undefined)
      .map((s) => s.column + " " + (s.asc ? "asc" : "desc"))
      .join(",");

    let locations = state.filters
      .filter((f) => f.name === "Location")
      .map((f) => f.value);
    let groupTypes = state.filters
      .filter((f) => f.name === "Group Type")
      .map((f) => f.value);

    updateState({ loading: true });
    let search = state.search ? state.search.trim() : "";
    let result;
    if (userId) {
      result = await actions.groups.groupGridForSpecificUser(
        search,
        locations,
        groupTypes,
        sort,
        page,
        pageSize,
        skipTotal,
        userId,
      );
      updateState({
        loading: false,
        currentPage: result.list,
        latestPage: result.list,
      });
    } else {
      result = await actions.groups.getGroupGrid(
        search,
        locations,
        groupTypes,
        sort,
        page,
        pageSize,
        skipTotal,
      );
      updateState({
        loading: false,
        currentPage: result?.list,
        latestPage: result?.list,
      });
    }

    // Show only one "No results found" toast at a time
    if (result?.list?.length === 0) {
      if (!noResultsToastShown) {
        showToast("error", "No results found");
        noResultsToastShown = true;
        setTimeout(() => {
          noResultsToastShown = false;
        }, 10000);
      }
    } else {
      noResultsToastShown = false;
    }

    return result;
  }

  function setSearch(search) {
    updateState({ search: search, gridReset: state.gridReset + 1 });
  }

  function onFilterChange(action, values) {
    if (action === "clear") {
      dispatch("GROUPS_FILTER_CLEARED");
      updateState({ filters: [], gridReset: state.gridReset + 1 });
    } else {
      let newFilters = state.filters.concat(values);
      updateState({ filters: newFilters, gridReset: state.gridReset + 1 });
    }
  }
  function onFilterChangeMobile(action, values) {
    if (action === "clear") {
      dispatch("GROUPS_FILTER_CLEARED");
      updateState({
        filters: [],
        gridReset: state.gridReset + 1,
        selectedValues: values,
      });
    } else {
      updateState({
        filters: values,
        gridReset: state.gridReset + 1,
        selectedValues: values,
      });
    }
  }
  function toggleAllCheckboxes() {
    let any = Object.keys(checkboxState).find((k) => checkboxState[k]);
    if (any || !state.currentPage) {
      setCheckboxState({});
      updateState({ isDeleteEnabled: false });
    } else {
      setCheckboxState(
        state.currentPage.reduce((newState, row) => {
          newState[row.id] = true;
          return newState;
        }, {}),
      );
      updateState({ isDeleteEnabled: true });
    }
  }
  function removeFilter(filter) {
    updateState({
      filters: state.filters.filter((f) => f !== filter),
      gridReset: state.gridReset + 1,
    });
  }

  function onSort(column) {
    let currentDirection = state.sort[column];
    let nextDirection =
      currentDirection === true
        ? false
        : currentDirection === false
          ? undefined
          : true;
    updateState({
      sort: { ...state.sort, [column]: nextDirection },
      gridReset: state.gridReset + 1,
    });
  }

  function onPageChanged(list) {
    setCheckboxState({});
    updateState({ currentPage: list });
  }

  function onEditMultipleClick(item) {
    if (item.name === "Merge Groups")
      updateState({ showMergeGroups: true, currentGroup: null });
    else if (item.name === "Delete Groups")
      updateState({ showDeleteGroupsModal: true, currentGroup: null });
  }

  function onColumnChange(item, show) {
    let cols = [...gridColumns];
    let i = cols.findIndex((c) => c.name === item.name);
    cols[i] = { ...cols[i], show: show };

    let list = cols.filter((c) => c.show).map((c) => c.column);
    storage.setObject("groups-columns", list);

    dispatch("GROUP_SETTINGS_UPDATE", { gridColumns: cols });
  }

  // Actions
  function goToSendAlert(groupId) {
    let groupIds = selectedRows;
    let groupIdsAsNumbers = groupId
      ? [groupId]
      : groupIds.map((id) => parseInt(id, 10));
    let groups = state.currentPage.filter((g) =>
      groupIdsAsNumbers.includes(g.id),
    );

    actions.alerts.showCreateAlert(false, groups, null, DISCARD_ROUTES.groups);
  }

  async function deleteGroups() {
    let groups = state.currentGroup ? [state.currentGroup.id] : selectedRows;
    updateState("saving", true);
    let result = await actions.groups.deleteGroups(groups);
    if (result?.success) {
      showToast("success", "Group deleted successfully");
      updateState({
        saving: false,
        showDeleteGroupsModal: false,
        currentGroup: null,
        gridReset: state.gridReset + 1,
      });
      dispatch("GROUP_LIST_CHANGED");
    } else updateState({ saving: false });
  }

  async function exportGroup() {
    let token = await actions.user.getShortTermToken();
    if (token) {
      downloadFile(
        "/api/groups/export?id=" +
          state.currentGroup.id +
          "&token=" +
          encodeURIComponent(token),
      );
      updateState("showExportModal", false);
    }
  }

  async function groupDetails(id, groupName, userCount) {
    if (isMobile) {
      return; // Prevent navigation on mobile
    }
    if (userCount > 0) {
      // Persist current filter so Groups page restores it on back navigation
      dispatch("GROUPS_FILTER_SAVED", {
        filters: state.filters,
        sort: state.sort,
      });
      let details = await actions.groups.getAllGroupMembers(id);
      history.push(`/group-members/${id}`, {
        groupDetails: details,
        groupName: groupName,
      });
    }
  }

  return (
    <PageLayout
      title={userId ? `${personName?.name}'s Groups` : "Groups"}
      backButton={!!userId}
      onBackClick={() => navigate(Path.PeopleDashboard.replace(":id", userId))}
      backButtonLabel={"Back to " + personName?.name + "'s Dashboard"}
      className="groups"
      buttonBar={
        <>
          {loginUser?.userType === UserRole.Admin && (
            <GuiButton
              dataQa="add-person-button"
              className=""
              onClick={(e) =>
                updateState({ showEditModal: true, currentGroup: {} })
              }
            >
              <Icon icon="plus" weight="400" />
              Group
            </GuiButton>
          )}
        </>
      }
    >
      <Card>
        {isMobile && (
          <>
            <div className="mobile-topbar-groups">
              <SearchBox
                onSearchChange={setSearch}
                searching={state.loading}
                className="mobile-topbar-groups-searchbox"
              />
              <GuiButton
                onClick={() => updateState({ isFilterIconClicked: true })}
              >
                <i className="fa fa-filter"></i>
              </GuiButton>
              {state.isFilterIconClicked && (
                <FilterPopup
                  filters={filterOptions}
                  selectedValues={state.selectedValues}
                  onClose={() =>
                    updateState({
                      isFilterIconClicked: false,
                    })
                  }
                  onFilterChange={onFilterChangeMobile}
                />
              )}
            </div>
            <div className="modal-footer-hr-divider"></div>
            {showSelection && (
              <div className="actions-btn-mobile">
                <GuiButton
                  className={"bulk-delete-btn" + (disabled ? " disabled" : "")}
                  onClick={(e) => {
                    e.stopPropagation();
                    goToSendAlert();
                  }}
                >
                  <i className="fa fa-send "></i> Send Message
                </GuiButton>
                <GuiButton
                  className={"bulk-delete-btn" + (disabled ? " disabled" : "")}
                  onClick={(e) => {
                    updateState({ showMergeGroups: true, currentGroup: null });
                  }}
                >
                  <i className="fa fa-merge "></i> Merge Groups
                </GuiButton>
                <GuiButton
                  className={"bulk-delete-btn" + (disabled ? " disabled" : "")}
                  onClick={(e) => {
                    e.stopPropagation();
                    updateState({
                      showActionsMenu: null,
                      showDeleteGroupsModal: true,
                    });
                  }}
                >
                  <i className="fa fa-trash"></i> Delete
                </GuiButton>
              </div>
            )}
            <div className="modal-footer-hr-divider"></div>
            {showSelection && (
              <div className="mobile-all-checkbox-selector">
                <CheckBoxHeaderButton
                  checked={Object.keys(checkboxState).find(
                    (k) => checkboxState[k]
                  )}
                  onClick={toggleAllCheckboxes}
                />
              </div>
            )}

            <div className="modal-footer-hr-divider"></div>
          </>
        )}
        {/* Desktop Search bar */}
        {!isMobile && (
          <div className="page-layout-content-header">
            <SearchBox onSearchChange={setSearch} searching={state.loading} />
            <FilterDropdown
              filters={filterOptions}
              onFilterChange={onFilterChange}
              selectedValues={state.selectedValues}
            />
            <div className="tools">
              {canManageGroups && (
                <EditMultiple
                  items={state.editMultipleItems}
                  onItemClick={onEditMultipleClick}
                  count={selectedRows.length}
                />
              )}
              <ColumnPicker items={gridColumns} onItemChange={onColumnChange} />
            </div>

            <FilterTagBar filters={state.filters} onRemove={removeFilter} />
          </div>
        )}

        {/* Grid */}
        <Grid
          header={
            <div className="header-row row">
              {showSelection && (
                <ColumnHeader className="check">
                  <CheckBoxHeaderButton
                    checked={Object.keys(checkboxState).find(
                      (k) => checkboxState[k]
                    )}
                    onClick={toggleAllCheckboxes}
                  />
                </ColumnHeader>
              )}
              <ColumnHeader
                className="groupname"
                sort={sort}
                onSort={onSort}
                visibility={visibility}
              >
                Group Name
              </ColumnHeader>
              <ColumnHeader
                className="location"
                sort={sort}
                onSort={onSort}
                visibility={visibility}
              >
                {locationName}
              </ColumnHeader>
              <ColumnHeader
                className="grouptype"
                sort={sort}
                onSort={onSort}
                visibility={visibility}
              >
                Type
              </ColumnHeader>
              <ColumnHeader
                className="groupid"
                sort={sort}
                onSort={onSort}
                visibility={visibility}
              >
                Group ID
              </ColumnHeader>
              <ColumnHeader
                className="alerts"
                sort={sort}
                onSort={onSort}
                visibility={visibility}
              >
                Messages
              </ColumnHeader>
              <ColumnHeader className="actions"></ColumnHeader>
            </div>
          }
          itemTemplate={(g) => (
            <div
              className={"row" + (g?.userCount > 0 ? " body-hover" : "")}
              key={g.id}
              onClick={() => groupDetails(g?.id, g?.name, g?.userCount)}
            >
              {showSelection && (
                <GridCell className="check">
                  <GuiCheckbox
                    name={"" + g.id}
                    value={checkboxState[g.id]}
                    onChange={updateCheckboxState}
                    onClick={(e) => e.stopPropagation()}
                  />
                </GridCell>
              )}
              <GridCell className="grid-initials">
                <span className="initials">
                  {(() => {
                    const chars = (g.name || "")
                      .replace(/[^a-zA-Z0-9]/g, "")
                      .toUpperCase();

                    if (chars.length === 0) {
                      return (
                        <i
                          className="fa fa-users"
                          style={{ color: "#ccc", fontSize: "1.2em" }}
                        ></i>
                      );
                    }
                    return chars.slice(0, 2);
                  })()}
                </span>
              </GridCell>
              <GridCell className="groupname" visibility={visibility}>
                {g.name || ""}
                <span>
                  {g.userCount} {pluralize("Member", g.userCount)}
                </span>
              </GridCell>
              <GridCell className="location" visibility={visibility}>
                {g.location || ""}
              </GridCell>
              <GridCell className="grouptype" visibility={visibility}>
                {g.groupTypeName || ""}
              </GridCell>
              <GridCell className="groupid" visibility={visibility}>
                {g.groupCode || ""}
              </GridCell>
              <GridCell className="alerts" visibility={visibility}>
                {g.alertCount || ""}
              </GridCell>
              <GridCell className="actions">
                {(isAdmin || isStaff) && (
                  <>
                    <i
                      className="far fa-ellipsis-h"
                      onClick={(e) => {
                        e.stopPropagation();
                        updateState("showActionsMenu", g.id);
                      }}
                    ></i>
                    <Popover
                      show={state.showActionsMenu === g.id}
                      className="no-arrow anchor-right popover-menu actions-menu"
                      onHide={() => updateState("showActionsMenu", null)}
                    >
                      <div
                        className="menu-item"
                        onClick={(e) => {
                          e.stopPropagation();
                          goToSendAlert(g.id);
                        }}
                      >
                        Send Message
                      </div>
                      {isAdmin && (
                        <>
                          <div
                            className="menu-item"
                            onClick={(e) => {
                              e.stopPropagation();
                              updateState({
                                showActionsMenu: null,
                                showExportModal: true,
                                currentGroup: g,
                              });
                            }}
                          >
                            Export
                          </div>
                          <div
                            className="menu-item"
                            onClick={(e) => {
                              e.stopPropagation();
                              updateState({
                                showActionsMenu: null,
                                showEditModal: true,
                                currentGroup: g,
                              });
                            }}
                          >
                            Edit
                          </div>
                          <div
                            className="menu-item"
                            onClick={(e) => {
                              e.stopPropagation();
                              updateState({
                                showActionsMenu: null,
                                showDeleteGroupsModal: true,
                                currentGroup: g,
                              });
                            }}
                          >
                            Delete
                          </div>
                        </>
                      )}
                    </Popover>
                  </>
                )}
              </GridCell>
            </div>
          )}
          mobileItemTemplate={(g) => (
            <div
              className="row"
              key={g.id}
              onClick={() => groupDetails(g.id, g.name, g.userCount)}
            >
              <div className="check">
                <div className="check-row">
                  {showSelection && (
                    <div className="check-title">
                      <GuiCheckbox
                        name={"" + g.id}
                        value={checkboxState[g.id]}
                        onChange={updateCheckboxState}
                      />
                    </div>
                  )}
                  <div className="check-body">
                    <div className="check-body-name">
                      <div className="check-body-name-header">
                        {g.name || ""}
                      </div>
                    </div>
                    <div className="check-body-details-mobile">
                      <div className="check-body-details-mobile-header">
                        <span>
                          {g.location}
                          <SeparatorDot />
                          {g.groupTypeName}
                          <SeparatorDot />
                          {g.userCount} {pluralize("Member", g.userCount)}
                        </span>
                      </div>
                      <div className="actions">
                        <Popover
                          show={state.showActionsMenu === g.id}
                          className="no-arrow anchor-right popover-menu actions-menu"
                          onHide={() => updateState("showActionsMenu", null)}
                        >
                          <div
                            className="menu-item"
                            onClick={(e) => {
                              e.stopPropagation();
                              goToSendAlert(g.id);
                            }}
                          >
                            Send Message
                          </div>
                          <div
                            className="menu-item"
                            onClick={(e) => {
                              updateState({
                                showActionsMenu: null,
                                showExportModal: true,
                                currentGroup: g,
                              });
                            }}
                          >
                            Export
                          </div>
                          <div
                            className="menu-item"
                            onClick={(e) =>
                              updateState({
                                showActionsMenu: null,
                                showEditModal: true,
                                currentGroup: g,
                              })
                            }
                          >
                            Edit
                          </div>

                          <div
                            className="menu-item"
                            onClick={(e) =>
                              updateState({
                                showActionsMenu: null,
                                showDeleteGroupsModal: true,
                                currentGroup: g,
                              })
                            }
                          >
                            Delete
                          </div>
                        </Popover>
                      </div>
                    </div>
                  </div>
                  <div className="tridot">
                    <i
                      className="fa fa-ellipsis-vertical mobile-action-icon"
                      onClick={(e) => {
                        e.stopPropagation(); // Prevents row click
                        updateState("showActionsMenu", g.id);
                      }}
                    ></i>
                  </div>
                </div>
              </div>
            </div>
          )}
          mobileItemHeight={65}
          reset={state.gridReset}
          pageSize={20}
          onItemsNeeded={loadRows}
          onPageChanged={onPageChanged}
          mobileFooter={(count) =>
            !state.loading
              ? count + " " + pluralize("Group", state?.totalCount)
              : "\u00A0"
          }
        ></Grid>
      </Card>
      {!useIsDesktop() && <BottomNavBar />}
      {/* Edit */}
      {state.showEditModal && (
        <GroupPropertiesModal
          group={state.currentGroup}
          onClose={(changed) =>
            updateState({
              showEditModal: false,
              gridReset: state.gridReset + (changed ? 1 : 0),
            })
          }
        />
      )}

      {/* Delete */}
      {state.showDeleteGroupsModal && (
        <Modal
          className="plain-modal no-close"
          showModal={state.showDeleteGroupsModal}
          onClose={() =>
            updateState({ showDeleteGroupsModal: false, currentGroup: null })
          }
        >
          <p>
            Are you sure you want to permanently remove{" "}
            {state.currentGroup ? "this group" : "these groups"} from the
            system?
          </p>
          <div className="buttons">
            <GuiButton
              className="btn-outline"
              onClick={(e) =>
                updateState({
                  showDeleteGroupsModal: false,
                  currentGroup: null,
                })
              }
            >
              Cancel
            </GuiButton>
            <GuiButton
              className="warning"
              onClick={deleteGroups}
              loading={state.saving}
            >
              Delete{" "}
              {state.currentGroup ? "Group" : selectedRows.length + " Groups"}
            </GuiButton>
          </div>
        </Modal>
      )}

      {/* Export */}
      {state.showExportModal && (
        <Modal
          className="plain-modal no-close"
          showModal={state.showExportModal}
          onClose={() =>
            updateState({ showExportModal: false, currentGroup: null })
          }
        >
          <p>Would you like to export people data to excel?</p>
          <div className="buttons">
            <GuiButton
              className="btn-outline"
              onClick={(e) =>
                updateState({ showExportModal: false, currentGroup: null })
              }
            >
              Cancel
            </GuiButton>
            <GuiButton
              className=""
              onClick={exportGroup}
              loading={state.saving}
            >
              Export
            </GuiButton>
          </div>
        </Modal>
      )}

      {/* Merge */}
      {state.showMergeGroups && (
        <MergeGroupsModal
          mergeSourceIds={selectedRows}
          onClose={(changed) =>
            updateState({
              showMergeGroups: false,
              gridReset: state.gridReset + (changed ? 1 : 0),
            })
          }
        />
      )}

      {/* Flyover */}
      {flyoverGroup && (
        <Modal
          className="flyover-container"
          onClose={() => setFlyoverGroup(null)}
        >
          {/* Display flyoverGroup data here */}
          <p>{flyoverGroup.name}</p>
          <button onClick={() => setFlyoverGroup(null)}>Close</button>
        </Modal>
      )}
    </PageLayout>
  );
}
